// Territorielle ansvarsområder på EKTE grenser: HV-områder følger kommunegrenser
// (union av kommunene sine) og distrikter følger fylkesgrenser. Hver kommune
// fargelegges etter hvilket HV-område/-distrikt som har ansvaret — eget mørkest,
// eget distrikt medium, resten lyst — så man straks ser eget ansvar og naboene.
// Kommuner uten konkret områdetildeling flagges (distrikt/FOH kan finfordele).
//
// Grensegeometri: apps/cop/public/geo/{kommuner,fylker}.geojson (Kartverket-
// avledet, forenklet). Ansvarskoblingen (kommune → område/distrikt) kommer fra
// @skade/contracts (hv-structure), så den er felles og testbar.
import type { Map as MlMap, GeoJSONSource, MapGeoJSONFeature } from 'maplibre-gl';
import { Popup } from 'maplibre-gl';
import {
  kommuneResponsibility, allAreas, FYLKE_NR_TO_DISTRICT,
  type AreaOfResponsibility,
} from '@skade/contracts';

const SRC_KOM = 'hv-kommuner';
const SRC_FYL = 'hv-fylker';
const SRC_OBJ = 'hv-aor-obj';
const SRC_LBL = 'hv-omr-label';
const RED = '#ff3b3b';

export interface AorScope {
  district?: string;
  areaId?: string;
  districtWide?: boolean;
}

type Tier = 'own' | 'district' | 'other';

export class TerritoryLayer {
  private scope: AorScope = {};
  private ownAor: AreaOfResponsibility | null = null;
  // AV som standard — hele landet rødt ble for rotete. Slås på via verktøylinja,
  // og viser da EGET område tydelig rødt, eget distrikt dempet, resten nøytralt.
  private visible = false;
  private ready = false;
  private kommuner: GeoJSON.FeatureCollection | null = null;
  private fylker: GeoJSON.FeatureCollection | null = null;
  private popup?: Popup;

  /**
   * `verktoyEierKlikk`: HV-kommuneflata var det laget som «lekket» klikk under
   * QA — den svarte med sin egen boble om hvem som har ansvaret midt i en
   * avstandsmåling og i en aksetegning, og satte pekeren fra trådkors til hånd.
   * Flata dekker hele eget område, så den treffer nesten alltid.
   */
  constructor(private map: MlMap, private verktoyEierKlikk: () => boolean = () => false) {
    void this.load();
    if (map.isStyleLoaded()) this.init();
    else map.on('load', () => this.init());
  }

  private async load() {
    try {
      const [k, f] = await Promise.all([
        fetch('/geo/kommuner.geojson').then((r) => r.json()),
        fetch('/geo/fylker.geojson').then((r) => r.json()),
      ]);
      this.kommuner = k; this.fylker = f;
      this.render();
    } catch (e) {
      console.warn('Territory: kunne ikke laste grensegeometri:', (e as Error).message);
    }
  }

  private init() {
    if (this.ready) return;
    for (const s of [SRC_KOM, SRC_FYL, SRC_OBJ, SRC_LBL]) {
      if (!this.map.getSource(s)) this.map.addSource(s, { type: 'geojson', data: empty() });
    }
    // Kommune-flate: KUN eget område + eget distrikt fylles (rest = ingen fyll, så
    // kartet ikke drukner i rødt). Eget mørkest, eget distrikt dempet.
    this.map.addLayer({
      id: 'hv-kom-fill', type: 'fill', source: SRC_KOM,
      paint: {
        'fill-color': RED,
        'fill-opacity': ['match', ['get', 'tier'], 'own', 0.42, 'district', 0.18, 0],
      },
    });
    // Kommunegrenser: eget/eget-distrikt i rødt; andre kommuner nøytral grå og svak.
    this.map.addLayer({
      id: 'hv-kom-line', type: 'line', source: SRC_KOM,
      paint: {
        'line-color': ['match', ['get', 'tier'], 'own', RED, 'district', RED, '#3a4048'],
        'line-width': ['match', ['get', 'tier'], 'own', 1.2, 'district', 0.7, 0.4],
        'line-opacity': ['match', ['get', 'tier'], 'own', 0.9, 'district', 0.5, 0.18],
      },
    });
    // Flagg: beste-anslag/ikke-tildelte kommuner — stiplet gul (bør bekreftes).
    this.map.addLayer({
      id: 'hv-kom-unassigned', type: 'line', source: SRC_KOM,
      filter: ['any', ['==', ['get', 'assigned'], false], ['==', ['get', 'estimated'], true]],
      paint: { 'line-color': '#ffd24a', 'line-width': 1.1, 'line-dasharray': [2, 2], 'line-opacity': 0.6 },
    });
    // Fylkesgrenser (distriktsnivå): eget distrikts fylke(r) tydeligere.
    this.map.addLayer({
      id: 'hv-fyl-line', type: 'line', source: SRC_FYL,
      paint: {
        'line-color': ['case', ['get', 'own'], RED, '#7a8894'],
        'line-width': ['case', ['get', 'own'], 2.2, 0.8],
        'line-opacity': ['case', ['get', 'own'], 0.9, 0.4],
      },
    });
    // Områdenavn (kun eget + eget distrikt, unngå å dekke hele landet i tekst).
    this.map.addLayer({
      id: 'hv-omr-name', type: 'symbol', source: SRC_LBL,
      layout: { 'text-field': ['get', 'name'], 'text-font': ['Open Sans Bold'], 'text-size': 12, 'text-transform': 'uppercase', 'text-letter-spacing': 0.05 },
      paint: { 'text-color': '#ffc4c4', 'text-halo-color': '#0a0c0a', 'text-halo-width': 1.5 },
    });
    // Nøkkelobjekter i eget område.
    this.map.addLayer({ id: 'hv-obj-dot', type: 'circle', source: SRC_OBJ, paint: { 'circle-radius': 5, 'circle-color': '#0a0c0a', 'circle-stroke-color': RED, 'circle-stroke-width': 2 } });
    this.map.addLayer({
      id: 'hv-obj-label', type: 'symbol', source: SRC_OBJ,
      layout: { 'text-field': ['get', 'name'], 'text-font': ['Open Sans Regular'], 'text-size': 11, 'text-offset': [0, 1.1], 'text-anchor': 'top' },
      paint: { 'text-color': '#ffb0b0', 'text-halo-color': '#0a0c0a', 'text-halo-width': 1.4 },
    });

    // Klikk på kommune → hvem har ansvaret.
    this.map.on('click', 'hv-kom-fill', (e) => {
      if (this.verktoyEierKlikk()) return;
      this.onKommuneClick(e.features?.[0], e.lngLat);
    });
    this.map.on('mouseenter', 'hv-kom-fill', () => {
      if (this.verktoyEierKlikk()) return;
      this.map.getCanvas().style.cursor = 'pointer';
    });
    this.map.on('mouseleave', 'hv-kom-fill', () => {
      if (this.verktoyEierKlikk()) return;
      this.map.getCanvas().style.cursor = '';
    });

    this.ready = true;
    this.render();
  }

  setScope(scope: AorScope, ownAor?: AreaOfResponsibility | null) {
    this.scope = scope;
    this.ownAor = ownAor ?? null;
    this.render();
    this.applyVisibility();
  }

  /** Av/på for rødmerkinga. Returnerer NY tilstand, så kalleren kan vise den. */
  toggle(): boolean { this.visible = !this.visible; this.applyVisibility(); return this.visible; }
  isVisible() { return this.visible; }

  fitToOwn() {
    const c = this.ownCenter();
    if (c) this.map.flyTo({ center: c, zoom: 8, duration: 800 });
  }
  private ownCenter(): [number, number] | undefined {
    const own = allAreas().find((a) => a.id === this.scope.areaId);
    return own?.center;
  }

  private tierFor(areaId?: string, district?: string): Tier {
    if (areaId && areaId === this.scope.areaId) return 'own';
    if (this.scope.districtWide && district && district === this.scope.district) return 'district';
    return 'other';
  }

  private ownFylkeNrs(): Set<string> {
    const s = new Set<string>();
    for (const [nr, d] of Object.entries(FYLKE_NR_TO_DISTRICT)) if (d === this.scope.district) s.add(nr);
    return s;
  }

  private applyVisibility() {
    if (!this.ready) return;
    const v = this.visible ? 'visible' : 'none';
    for (const id of ['hv-kom-fill', 'hv-kom-line', 'hv-kom-unassigned', 'hv-fyl-line', 'hv-omr-name', 'hv-obj-dot', 'hv-obj-label']) {
      if (this.map.getLayer(id)) this.map.setLayoutProperty(id, 'visibility', v);
    }
  }

  private render() {
    if (!this.ready) return;
    // Kommuner: annotér tier + ansvar per kommune.
    if (this.kommuner) {
      const feats = this.kommuner.features.map((f) => {
        const p = (f.properties ?? {}) as Record<string, unknown>;
        const name = String(p.name ?? p.kommunenavn ?? '');
        const knr = String(p.kommunenummer ?? p.id ?? '');
        const r = kommuneResponsibility(name, knr);
        return { ...f, properties: { name, kommunenummer: knr, areaId: r.areaId ?? '', district: r.district ?? '', assigned: r.assigned, estimated: r.estimated, tier: this.tierFor(r.areaId, r.district) } };
      });
      (this.map.getSource(SRC_KOM) as GeoJSONSource).setData({ type: 'FeatureCollection', features: feats } as never);
    }
    // Fylker: marker eget distrikts fylke(r).
    if (this.fylker) {
      const own = this.ownFylkeNrs();
      const feats = this.fylker.features.map((f) => {
        const p = (f.properties ?? {}) as Record<string, unknown>;
        const fnr = String(p.fylkesnummer ?? p.id ?? '');
        return { ...f, properties: { ...p, own: own.has(fnr) } };
      });
      (this.map.getSource(SRC_FYL) as GeoJSONSource).setData({ type: 'FeatureCollection', features: feats } as never);
    }
    // Områdenavn (eget + eget distrikt) ved skjematisk senter.
    const labels = allAreas()
      .filter((a) => !!a.center && this.tierFor(a.id, a.district) !== 'other')
      .map((a) => ({ type: 'Feature' as const, geometry: { type: 'Point' as const, coordinates: a.center! }, properties: { name: a.name } }));
    (this.map.getSource(SRC_LBL) as GeoJSONSource).setData({ type: 'FeatureCollection', features: labels } as never);
    // Nøkkelobjekter i eget område.
    const obj = this.ownAor?.keyObjects ?? [];
    (this.map.getSource(SRC_OBJ) as GeoJSONSource).setData({
      type: 'FeatureCollection',
      features: obj.map((o) => ({ type: 'Feature', geometry: { type: 'Point', coordinates: [o.lon, o.lat] }, properties: { name: o.name } })),
    } as never);
  }

  private onKommuneClick(f: MapGeoJSONFeature | undefined, lngLat: { lng: number; lat: number }) {
    if (!f) return;
    const p = f.properties as Record<string, unknown>;
    const name = String(p.name ?? '');
    const areaId = String(p.areaId ?? '');
    const district = String(p.district ?? '');
    const assigned = p.assigned === true || p.assigned === 'true';
    const estimated = p.estimated === true || p.estimated === 'true';
    const areaName = assigned ? (allAreas().find((a) => a.id === areaId)?.name ?? areaId) : '';
    const ansvar = assigned
      ? `HV-område: <b>${esc(areaName)}</b> (${esc(areaId)})${estimated ? '<br><i>Beste-anslag — bekreftes/justeres av distrikt/FOH</i>' : ''}`
      : `Distrikt: <b>${esc(district || '—')}</b><br><i>Område ikke tildelt — settes av distrikt/FOH</i>`;
    this.popup?.remove();
    this.popup = new Popup({ closeButton: true, offset: 8 })
      .setLngLat(lngLat)
      .setHTML(`<div style="font:12px system-ui;color:#eaeef2"><b>${esc(name)}</b><br>${ansvar}</div>`)
      .addTo(this.map);
  }
}

function empty() { return { type: 'FeatureCollection', features: [] } as never; }
function esc(s: string): string { return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string)); }
